import React from "react";
import { createRoot } from "react-dom/client";
import posthog from "posthog-js";
import * as Sentry from "@sentry/react";
import App from "./App.tsx";
import "./index.css";
import { useAnalyticsConsent } from "@/hooks/useAnalyticsConsent";
import { applyAnalyticsConsent } from "@/lib/analyticsConsent";
import { maybeInitRewardful } from "@/lib/rewardful";

let posthogInitialized = false;

const sendDefaultPii = import.meta.env.VITE_SENTRY_SEND_DEFAULT_PII === "true";
const enableLogs = import.meta.env.VITE_SENTRY_ENABLE_LOGS === "true";
const sentryIntegrations = enableLogs 
  ? [Sentry.browserTracingIntegration(), Sentry.consoleLoggingIntegration({ levels: ["log", "warn", "error"] })]
  : [Sentry.browserTracingIntegration()];

// Logs/telemetry are configured to respect privacy controls and avoid unnecessary PII.
Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  // Setting this option to true will send default PII data to Sentry.
  // For example, automatic IP address collection on events
  sendDefaultPii,
  integrations: sentryIntegrations,
  // Tracing
  tracesSampleRate: 1.0, //  Capture 100% of the transactions
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: ["localhost", /^https:\/\/yourserver\.io\/api/],
  // Enable logs to be sent to Sentry
  enableLogs,
});

const AnalyticsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const consent = useAnalyticsConsent();

  React.useEffect(() => {
    if (consent) {
      applyAnalyticsConsent(consent);
    }
    maybeInitRewardful(consent);

    if (consent === "accepted" && !posthogInitialized) {
      // Analytics events only fire after consent (Privacy Policy: analytics data handling).
      posthog.init(import.meta.env.VITE_POSTHOG_KEY, {
        api_host: import.meta.env.VITE_POSTHOG_HOST,
        autocapture: true,
        capture_pageview: true,
        defaults: "2025-05-24",
        capture_exceptions: true,
        debug: import.meta.env.MODE === "development",
      });
      posthogInitialized = true;
    } else if (consent === "declined" && posthogInitialized) {
      posthog.opt_out_capturing();
    }
  }, [consent]);

  return <>{children}</>;
};

const container = document.getElementById("root") as HTMLElement;
const existingRoot = (container as HTMLElement & { _reactRoot?: ReturnType<typeof createRoot> })._reactRoot;
const root = existingRoot ?? createRoot(container);
(container as HTMLElement & { _reactRoot?: ReturnType<typeof createRoot> })._reactRoot = root;

root.render(
  <React.StrictMode>
    <AnalyticsProvider>
      <App />
    </AnalyticsProvider>
  </React.StrictMode>
);
